import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { ScorecardData, AuthState, Metric, UserRole, OrgChartData, OrgChartPerson, GapData, GapItem } from '../types/scorecard';
import { DatabaseService } from '../services/supabase';
import { useLiveEdit } from '../hooks/useLiveEdit';
import { getInitialState as getLegacyInitialState } from './SimpleAppContext';
import { scorecardData4 } from '../data/scorecardData4';
import { defaultOrgChartData } from '../data/orgChartData';

interface LiveAppState {
 scorecard: ScorecardData;
 orgChart: OrgChartData;
 gapData: GapData;
 authState: AuthState;
 currentView: 'scorecard' | 'orgchart' | 'gap';
 isLoading: boolean;
 isSaving: boolean;
 lastSaved: Date | null;
 isOnline: boolean;
 error: string | null;
}

interface LiveAppContextType extends LiveAppState {
 updateMetric: (categoryId: string, level: number, metricId: string, updates: Partial<Metric>) => Promise<void>;
 updateHierarchicalMetric: (categoryId: string, metricPath: string[], updates: Partial<Metric>) => Promise<void>;
 addMetric: (categoryId: string, level: number, metric: Omit<Metric, 'id' | 'lastUpdated'>) => Promise<void>;
 removeMetric: (categoryId: string, level: number, metricId: string) => Promise<void>;
 updateOrgChartPerson: (personId: string, updates: Partial<OrgChartPerson>) => Promise<void>;
 addOrgChartPerson: (person: Omit<OrgChartPerson, 'id' | 'lastUpdated'>) => Promise<void>;
 removeOrgChartPerson: (personId: string) => Promise<void>;
 updateGapItem: (gapId: string, updates: Partial<GapItem>) => Promise<void>;
 addGapItem: (gap: Omit<GapItem, 'id' | 'lastUpdated'>) => Promise<void>;
 removeGapItem: (gapId: string) => Promise<void>;
 setCurrentView: (view: 'scorecard' | 'orgchart' | 'gap') => void;
 saveAll: () => Promise<void>;
 login: (email: string, password: string) => Promise<boolean>;
 logout: () => Promise<void>;
 signUp: (email: string, password: string) => Promise<boolean>;
 resetCloudData: () => Promise<void>;
}

type LiveAppAction = 
 | { type: 'SET_LOADING'; payload: boolean }
 | { type: 'SET_SAVING'; payload: boolean }
 | { type: 'SET_ERROR'; payload: string | null }
 | { type: 'SET_LAST_SAVED'; payload: Date | null }
 | { type: 'SET_ONLINE'; payload: boolean }
 | { type: 'UPDATE_SCORECARD'; payload: ScorecardData }
 | { type: 'UPDATE_ORG_CHART'; payload: OrgChartData }
 | { type: 'UPDATE_GAP_DATA'; payload: GapData }
 | { type: 'SET_AUTH_STATE'; payload: AuthState }
 | { type: 'SET_CURRENT_VIEW'; payload: 'scorecard' | 'orgchart' | 'gap' };

function getLiveInitialState(): LiveAppState {
 // Avoid calling this at module evaluation time due to circular dependency.
 const legacy = getLegacyInitialState();
 return {
  // Directly use the raw source file to bypass any stale localStorage cache
  scorecard: scorecardData4,
  orgChart: defaultOrgChartData, // Use the full org chart data instead of legacy
  gapData: legacy.gapData,
  authState: { isAuthenticated: false },
  currentView: 'scorecard',
  isLoading: true,
  isSaving: false,
  lastSaved: null,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  error: null
 };
}

function liveAppReducer(state: LiveAppState, action: LiveAppAction): LiveAppState {
 switch (action.type) {
  case 'SET_LOADING':
   return { ...state, isLoading: action.payload };
  case 'SET_SAVING':
   return { ...state, isSaving: action.payload };
  case 'SET_ERROR':
   return { ...state, error: action.payload };
  case 'SET_LAST_SAVED':
   return { ...state, lastSaved: action.payload };
  case 'SET_ONLINE':
   return { ...state, isOnline: action.payload };
  case 'UPDATE_SCORECARD':
   return { ...state, scorecard: action.payload };
  case 'UPDATE_ORG_CHART':
   return { ...state, orgChart: action.payload };
  case 'UPDATE_GAP_DATA':
   return { ...state, gapData: action.payload };
  case 'SET_AUTH_STATE':
   return { ...state, authState: action.payload };
  case 'SET_CURRENT_VIEW':
   return { ...state, currentView: action.payload };
  default:
   return state;
 }
}

export const LiveAppContext = createContext<LiveAppContextType | undefined>(undefined);

export function LiveAppProvider({ children }: { children: React.ReactNode }) {
 const [state, dispatch] = useReducer(liveAppReducer, undefined as any, getLiveInitialState);

 // Initialize live edit hook
 const liveEdit = useLiveEdit(state.scorecard, (updatedData) => {
  dispatch({ type: 'UPDATE_SCORECARD', payload: updatedData });
 });

 // Check authentication status on mount
 useEffect(() => {
  const checkAuth = async () => {
   try {
    // Add timeout to prevent infinite loading
    const timeoutPromise = new Promise((_, reject) => 
     setTimeout(() => reject(new Error('Auth check timeout')), 5000)
    );
    
    const user = await Promise.race([
     DatabaseService.getCurrentUser(),
     timeoutPromise
    ]);
    
    if (user) {
     dispatch({ 
      type: 'SET_AUTH_STATE', 
      payload: { 
       isAuthenticated: true, 
       user: { name: (user as any)?.email || 'User', role: 'admin' as UserRole }
      }
     });
    }
   } catch (error) {
    console.error('Auth check failed:', error);
    // Set loading to false even on error so user can see login form
    dispatch({ type: 'SET_LOADING', payload: false });
   } finally {
    // Always stop loading after 5 seconds max
    setTimeout(() => {
     dispatch({ type: 'SET_LOADING', payload: false });
    }, 100);
   }
  };

  checkAuth();
 }, []);

 // Load data on mount and when authenticated
 useEffect(() => {
  const loadData = async () => {
   if (!state.authState.isAuthenticated) return;

   dispatch({ type: 'SET_LOADING', payload: true });
   try {
    console.log('LiveApp: Loading all data from Supabase...');
    const [scorecardData, metricUpdates, orgChartData, gapData] = await Promise.all([
     DatabaseService.loadScorecardData(),
     DatabaseService.getMetricUpdates(),
     DatabaseService.loadOrgChartData(),
     DatabaseService.loadGapData()
    ]);

    // Load scorecard data
    if (scorecardData && scorecardData.data && scorecardData.data.currentVersion && scorecardData.data.currentVersion.length > 0) {
     const updatedScorecard = applyMetricUpdates(scorecardData.data, metricUpdates);
     dispatch({ type: 'UPDATE_SCORECARD', payload: updatedScorecard });
     console.log('LiveApp: Loaded scorecard data from Supabase');
    } else {
     await DatabaseService.saveScorecardData(scorecardData4);
     console.log('LiveApp: Seeded new scorecard data');
    }

    // Load org chart data
    if (orgChartData && orgChartData.data) {
     dispatch({ type: 'UPDATE_ORG_CHART', payload: orgChartData.data });
     console.log('LiveApp: Loaded org chart data from Supabase');
    } else {
     await DatabaseService.saveOrgChartData(defaultOrgChartData);
     console.log('LiveApp: Seeded new org chart data');
    }

    // Load gap analysis data
    if (gapData && gapData.data) {
     dispatch({ type: 'UPDATE_GAP_DATA', payload: gapData.data });
     console.log('LiveApp: Loaded gap analysis data from Supabase');
    } else {
     const legacy = getLegacyInitialState();
     await DatabaseService.saveGapData(legacy.gapData);
     console.log('LiveApp: Seeded new gap analysis data');
    }

    dispatch({ type: 'SET_ERROR', payload: null });
    console.log('LiveApp: All data loaded successfully');
   } catch (error) {
    console.error('Failed to load data:', error);
    dispatch({ type: 'SET_ERROR', payload: 'Failed to load dashboard data' });
   } finally {
    dispatch({ type: 'SET_LOADING', payload: false });
   }
  };

  loadData();
 }, [state.authState.isAuthenticated]);

 // Set up real-time subscriptions
 useEffect(() => {
  if (!state.authState.isAuthenticated) return;

  const subscription = DatabaseService.subscribeToScorecardChanges((payload) => {
   console.log('Real-time update:', payload);
   // Reload data when changes occur
   // In a production app, you'd want more granular updates
  });

  return () => {
   subscription.unsubscribe();
  };
 }, [state.authState.isAuthenticated]);

 // Track online status
 useEffect(() => {
  const handleOnline = () => dispatch({ type: 'SET_ONLINE', payload: true });
  const handleOffline = () => dispatch({ type: 'SET_ONLINE', payload: false });

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
   window.removeEventListener('online', handleOnline);
   window.removeEventListener('offline', handleOffline);
  };
 }, []);

 // Helper function to apply metric updates to scorecard data
 const applyMetricUpdates = (scorecardData: any, metricUpdates: any[]): ScorecardData => {
  if (!metricUpdates || metricUpdates.length === 0) return scorecardData;

  const updateMap = new Map(metricUpdates.map(update => [update.metric_id, update]));

  const applyUpdatesRecursive = (metrics: Metric[]): Metric[] => {
   return metrics.map(metric => {
    const update = updateMap.get(metric.id);
    const updatedMetric = update ? {
     ...metric,
     value: update.value,
     target: update.target,
     lastUpdated: new Date(update.last_updated)
    } : metric;

    if (updatedMetric.subMetrics && updatedMetric.subMetrics.length > 0) {
     updatedMetric.subMetrics = applyUpdatesRecursive(updatedMetric.subMetrics);
    }

    return updatedMetric;
   });
  };

  return {
   ...scorecardData,
   currentVersion: scorecardData.currentVersion.map((category: any) => ({
    ...category,
    levels: category.levels.map((level: any) => ({
     ...level,
     metrics: applyUpdatesRecursive(level.metrics)
    }))
   }))
  };
 };

 // Helper function to update metric in data structure
 const updateMetricInData = (data: any, metricId: string, updates: Partial<Metric>): any => {
  const updateMetricRecursive = (metrics: Metric[]): Metric[] => {
   return metrics.map(metric => {
    if (metric.id === metricId) {
     return {
      ...metric,
      ...updates,
      lastUpdated: new Date()
     };
    }
    if (metric.subMetrics && metric.subMetrics.length > 0) {
     return {
      ...metric,
      subMetrics: updateMetricRecursive(metric.subMetrics)
     };
    }
    return metric;
   });
  };

  return {
   ...data,
   currentVersion: data.currentVersion.map((category: any) => ({
    ...category,
    levels: category.levels.map((level: any) => ({
     ...level,
     metrics: updateMetricRecursive(level.metrics)
    }))
   })),
   lastModified: new Date()
  };
 };

 // Helper function to add metric to data structure
 const addMetricInData = (data: any, parentId: string, level: number, newMetric: Metric): any => {
  const addMetricRecursive = (metrics: Metric[], targetParentId: string): Metric[] => {
   return metrics.map(metric => {
    if (metric.id === targetParentId) {
     return {
      ...metric,
      subMetrics: [...(metric.subMetrics || []), newMetric],
      lastUpdated: new Date()
     };
    }
    if (metric.subMetrics && metric.subMetrics.length > 0) {
     return {
      ...metric,
      subMetrics: addMetricRecursive(metric.subMetrics, targetParentId)
     };
    }
    return metric;
   });
  };

  // If level is 1, add to category level
  if (level === 1) {
   return {
    ...data,
    currentVersion: data.currentVersion.map((category: any) => ({
     ...category,
     levels: category.id === parentId ? category.levels.map((level: any) => ({
      ...level,
      metrics: [...level.metrics, newMetric]
     })) : category.levels
    })),
    lastModified: new Date()
   };
  }

  // For deeper levels, add to specific parent metric
  return {
   ...data,
   currentVersion: data.currentVersion.map((category: any) => ({
    ...category,
    levels: category.levels.map((level: any) => ({
     ...level,
     metrics: addMetricRecursive(level.metrics, parentId)
    }))
   })),
   lastModified: new Date()
  };
 };

 const contextValue: LiveAppContextType = {
  ...state,
  isLoading: state.isLoading || liveEdit.isLoading,
  isSaving: state.isSaving || liveEdit.isSaving,
  lastSaved: state.lastSaved || liveEdit.lastSaved,
  error: state.error || liveEdit.error,

  updateMetric: async (categoryId: string, level: number, metricId: string, updates: Partial<Metric>) => {
   try {
    console.log('LiveApp: Updating metric', metricId, updates);
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_SAVING', payload: true });
    
    // Update local state immediately
    const updatedData = updateMetricInData(state.scorecard, metricId, updates);
    dispatch({ type: 'UPDATE_SCORECARD', payload: updatedData });

    // Save to database with auto-save
    if (updates.value !== undefined || updates.target !== undefined) {
     console.log('LiveApp: Saving value/target changes via liveEdit');
     if (updates.value !== undefined) await liveEdit.updateMetric(metricId, 'value', updates.value || '');
     if (updates.target !== undefined) await liveEdit.updateMetric(metricId, 'target', updates.target);
    }
    
    // For structural changes like isActive, save the full JSON directly
    if (updates.isActive !== undefined) {
     console.log('LiveApp: Saving isActive change directly to database');
     await DatabaseService.saveScorecardData(updatedData);
     const savedTime = new Date();
     dispatch({ type: 'SET_LAST_SAVED', payload: savedTime });
     console.log('LiveApp: SET_LAST_SAVED dispatched with:', savedTime);
    }
    
    console.log('LiveApp: Update completed successfully');
   } catch (error) {
    console.error('LiveApp: Update failed', error);
    dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Update failed' });
   } finally {
    dispatch({ type: 'SET_SAVING', payload: false });
   }
  },

  updateHierarchicalMetric: async (categoryId: string, metricPath: string[], updates: Partial<Metric>) => {
   // Route to updateMetric for now
   if (metricPath.length > 0) {
    await contextValue.updateMetric(categoryId, 1, metricPath[0], updates);
   }
  },

  addMetric: async (categoryId: string, level: number, metric: Omit<Metric, 'id' | 'lastUpdated'>) => {
   try {
    dispatch({ type: 'SET_ERROR', payload: null });
    
    const newMetric: Metric = {
     ...metric,
     id: `metric-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
     lastUpdated: new Date(),
     subMetrics: []
    };

    // The parentId: if it's level 1, parent is categoryId. Else it's the categoryId param which we'll repurpose as parentId for deeply nested levels
    // In HierarchicalScorecard, we will pass the parentId here
    const updatedData = addMetricInData(state.scorecard, categoryId, level, newMetric);
    dispatch({ type: 'UPDATE_SCORECARD', payload: updatedData });

    await DatabaseService.saveScorecardData(updatedData);
   } catch (error) {
    dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to add metric' });
   }
  },

  removeMetric: async (categoryId: string, level: number, metricId: string) => {
   // Implementation for removing metrics
   console.log('Removing metric:', metricId);
  },

  updateOrgChartPerson: async (personId: string, updates: Partial<OrgChartPerson>) => {
   try {
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_SAVING', payload: true });
    
    const updatedPeople = state.orgChart.people.map(person => 
     person.id === personId ? { ...person, ...updates, lastUpdated: new Date() } : person
    );
    const newOrgChart = { ...state.orgChart, people: updatedPeople, lastModified: new Date() };
    
    // Update local state immediately
    dispatch({ type: 'UPDATE_ORG_CHART', payload: newOrgChart });
    
    // Save to Supabase
    await DatabaseService.saveOrgChartData(newOrgChart);
    dispatch({ type: 'SET_LAST_SAVED', payload: new Date() });
    
    console.log('LiveApp: Org chart person updated successfully');
   } catch (error) {
    console.error('LiveApp: Failed to update org chart person', error);
    dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to update person' });
   } finally {
    dispatch({ type: 'SET_SAVING', payload: false });
   }
  },

  addOrgChartPerson: async (person: Omit<OrgChartPerson, 'id' | 'lastUpdated'>) => {
   try {
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_SAVING', payload: true });
    
    const newPerson = {
     ...person,
     id: `person-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
     lastUpdated: new Date()
    };
    const newOrgChart = { ...state.orgChart, people: [...state.orgChart.people, newPerson], lastModified: new Date() };
    
    // Update local state immediately
    dispatch({ type: 'UPDATE_ORG_CHART', payload: newOrgChart });
    
    // Save to Supabase
    await DatabaseService.saveOrgChartData(newOrgChart);
    dispatch({ type: 'SET_LAST_SAVED', payload: new Date() });
    
    console.log('LiveApp: Org chart person added successfully');
   } catch (error) {
    console.error('LiveApp: Failed to add org chart person', error);
    dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to add person' });
   } finally {
    dispatch({ type: 'SET_SAVING', payload: false });
   }
  },

  removeOrgChartPerson: async (personId: string) => {
   try {
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_SAVING', payload: true });
    
    const updatedPeople = state.orgChart.people.filter(p => p.id !== personId);
    const newOrgChart = { ...state.orgChart, people: updatedPeople, lastModified: new Date() };
    
    // Update local state immediately
    dispatch({ type: 'UPDATE_ORG_CHART', payload: newOrgChart });
    
    // Save to Supabase
    await DatabaseService.saveOrgChartData(newOrgChart);
    dispatch({ type: 'SET_LAST_SAVED', payload: new Date() });
    
    console.log('LiveApp: Org chart person removed successfully');
   } catch (error) {
    console.error('LiveApp: Failed to remove org chart person', error);
    dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to remove person' });
   } finally {
    dispatch({ type: 'SET_SAVING', payload: false });
   }
  },

  updateGapItem: async (gapId: string, updates: Partial<GapItem>) => {
   try {
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_SAVING', payload: true });
    
    const updatedGaps = state.gapData.gaps.map(gap => 
     gap.id === gapId ? { ...gap, ...updates, lastUpdated: new Date() } : gap
    );
    const newGapData = { ...state.gapData, gaps: updatedGaps, lastModified: new Date() };
    
    // Update local state immediately
    dispatch({ type: 'UPDATE_GAP_DATA', payload: newGapData });
    
    // Save to Supabase
    await DatabaseService.saveGapData(newGapData);
    dispatch({ type: 'SET_LAST_SAVED', payload: new Date() });
    
    console.log('LiveApp: Gap item updated successfully');
   } catch (error) {
    console.error('LiveApp: Failed to update gap item', error);
    dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to update gap item' });
   } finally {
    dispatch({ type: 'SET_SAVING', payload: false });
   }
  },

  addGapItem: async (gap: Omit<GapItem, 'id' | 'lastUpdated'>) => {
   try {
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_SAVING', payload: true });
    
    const newGap = {
     ...gap,
     id: `gap-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
     lastUpdated: new Date()
    };
    const newGapData = { ...state.gapData, gaps: [...state.gapData.gaps, newGap], lastModified: new Date() };
    
    // Update local state immediately
    dispatch({ type: 'UPDATE_GAP_DATA', payload: newGapData });
    
    // Save to Supabase
    await DatabaseService.saveGapData(newGapData);
    dispatch({ type: 'SET_LAST_SAVED', payload: new Date() });
    
    console.log('LiveApp: Gap item added successfully');
   } catch (error) {
    console.error('LiveApp: Failed to add gap item', error);
    dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to add gap item' });
   } finally {
    dispatch({ type: 'SET_SAVING', payload: false });
   }
  },

  removeGapItem: async (gapId: string) => {
   try {
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_SAVING', payload: true });
    
    const updatedGaps = state.gapData.gaps.filter(g => g.id !== gapId);
    const newGapData = { ...state.gapData, gaps: updatedGaps, lastModified: new Date() };
    
    // Update local state immediately
    dispatch({ type: 'UPDATE_GAP_DATA', payload: newGapData });
    
    // Save to Supabase
    await DatabaseService.saveGapData(newGapData);
    dispatch({ type: 'SET_LAST_SAVED', payload: new Date() });
    
    console.log('LiveApp: Gap item removed successfully');
   } catch (error) {
    console.error('LiveApp: Failed to remove gap item', error);
    dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to remove gap item' });
   } finally {
    dispatch({ type: 'SET_SAVING', payload: false });
   }
  },

  setCurrentView: (view: 'scorecard' | 'orgchart' | 'gap') => {
   dispatch({ type: 'SET_CURRENT_VIEW', payload: view });
  },

  saveAll: async () => {
   await liveEdit.saveAll();
   dispatch({ type: 'SET_LAST_SAVED', payload: new Date() });
  },

  resetCloudData: async () => {
   if (window.confirm("Are you sure you want to completely overwrite your cloud database with the local template file?")) {
    try {
     dispatch({ type: 'SET_LOADING', payload: true });
     await DatabaseService.saveScorecardData(scorecardData4);
     dispatch({ type: 'UPDATE_SCORECARD', payload: scorecardData4 });
     alert("Cloud data reset successfully! Your dashboard is now synced perfectly to your code file.");
    } catch (error) {
     console.error("Failed to reset cloud data:", error);
     alert("Failed to reset cloud data. Check console.");
    } finally {
     dispatch({ type: 'SET_LOADING', payload: false });
    }
   }
  },

  login: async (email: string, password: string) => {
   try {
    dispatch({ type: 'SET_ERROR', payload: null });
    const result = await DatabaseService.signIn(email, password);
    if (result.user) {
     dispatch({ 
      type: 'SET_AUTH_STATE', 
      payload: { 
       isAuthenticated: true, 
       user: { name: (result.user as any)?.email || 'User', role: 'admin' as UserRole }
      }
     });
     return true;
    }
    return false;
   } catch (error) {
    dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Login failed' });
    return false;
   }
  },

  logout: async () => {
   try {
    await DatabaseService.signOut();
    dispatch({ type: 'SET_AUTH_STATE', payload: { isAuthenticated: false } });
   } catch (error) {
    console.error('Logout failed:', error);
   }
  },

  signUp: async (email: string, password: string) => {
   try {
    dispatch({ type: 'SET_ERROR', payload: null });
    const result = await DatabaseService.signUp(email, password);
    if (result.user) {
     dispatch({ 
      type: 'SET_AUTH_STATE', 
      payload: { 
       isAuthenticated: true, 
       user: { name: (result.user as any)?.email || 'User', role: 'admin' as UserRole }
      }
     });
     return true;
    }
    return false;
   } catch (error) {
    dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Sign up failed' });
    return false;
   }
  }
 };

 return (
  <LiveAppContext.Provider value={contextValue}>
   {children}
  </LiveAppContext.Provider>
 );
}

export function useLiveApp() {
 const context = useContext(LiveAppContext);
 if (context === undefined) {
  throw new Error('useLiveApp must be used within a LiveAppProvider');
 }
 return context;
}